import { useState, useEffect } from 'react';
import api from './api';
import './AccountManagement.css';

const VEHICLE_LABELS = { 0: '미분류', 1: 'eVTOL', 2: '멀티콥터', 3: '틸트로터' };
const STATUS_LABELS  = { 0: '중단', 1: '운영중' };
const fmtDate = v => v ? v.replace('T', ' ') : '-';
const EMPTY_CREATE = { aircraftId: '', modelName: '', manufacturer: '', vehicleType: 0, maxSpeed: 0, status: 1 };

export default function AircraftManagement() {
  const [aircrafts, setAircrafts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [mode, setMode]           = useState('view'); // 'view' | 'create'

  const [editForm, setEditForm]     = useState({ modelName: '', manufacturer: '', vehicleType: 0, maxSpeed: 0, status: 1 });
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [saving, setSaving]               = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [msg, setMsg]                     = useState({ text: '', type: '' });

  useEffect(() => { fetchList(); }, []);

  async function fetchList() {
    try {
      const res = await api.get('/api/aircraft/list');
      setAircrafts(res.data);
    } catch {
      setMsg({ text: '기체 목록을 불러오지 못했습니다.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function selectAircraft(ac) {
    setSelected(ac);
    setEditForm({ modelName: ac.modelName, manufacturer: ac.manufacturer, vehicleType: ac.vehicleType, maxSpeed: ac.maxSpeed, status: ac.status });
    setMode('view');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function openCreate() {
    setSelected(null);
    setCreateForm(EMPTY_CREATE);
    setMode('create');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function setEdit(key, val)   { setEditForm(f => ({ ...f, [key]: val })); }
  function setCreate(key, val) { setCreateForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const body = {
        modelName:    editForm.modelName,
        manufacturer: editForm.manufacturer,
        vehicleType:  Number(editForm.vehicleType),
        maxSpeed:     Number(editForm.maxSpeed),
        status:       Number(editForm.status),
      };
      const res = await api.put(`/api/aircraft/${selected.aircraftUid}`, body);
      if (res.data.success !== false) {
        setMsg({ text: '수정되었습니다.', type: 'success' });
        const updated = { ...selected, ...body };
        setAircrafts(prev => prev.map(a => a.aircraftUid === selected.aircraftUid ? updated : a));
        setSelected(updated);
      } else {
        setMsg({ text: res.data.message || '수정에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate() {
    const { aircraftId, modelName, manufacturer } = createForm;
    if (!aircraftId.trim() || !modelName.trim() || !manufacturer.trim()) {
      setMsg({ text: '기체 ID, 모델명, 제조사는 필수입니다.', type: 'error' });
      return;
    }
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/api/aircraft/create', {
        aircraftId:   aircraftId.trim(),
        modelName:    modelName.trim(),
        manufacturer: manufacturer.trim(),
        vehicleType:  Number(createForm.vehicleType),
        maxSpeed:     Number(createForm.maxSpeed),
        status:       Number(createForm.status),
      });
      if (res.data.success) {
        setMsg({ text: '기체가 등록되었습니다.', type: 'success' });
        await fetchList();
        setMode('view');
        setCreateForm(EMPTY_CREATE);
      } else {
        setMsg({ text: res.data.message || '등록에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.delete(`/api/aircraft/${selected.aircraftUid}`);
      if (res.data.success !== false) {
        setAircrafts(prev => prev.filter(a => a.aircraftUid !== selected.aircraftUid));
        setSelected(null);
        setMode('view');
        setConfirmDelete(false);
      } else {
        setMsg({ text: res.data.message || '삭제에 실패했습니다.', type: 'error' });
        setConfirmDelete(false);
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="acm">
      {/* ── 기체 목록 ── */}
      <div className="acm-list">
        <div className="acm-list-hd">
          <span>기체 목록 {!loading && `(${aircrafts.length})`}</span>
          <button className="acm-create-btn" onClick={openCreate}>+ 기체 등록</button>
        </div>
        {loading ? (
          <div className="acm-placeholder">불러오는 중...</div>
        ) : aircrafts.length === 0 ? (
          <div className="acm-placeholder">등록된 기체가 없습니다.</div>
        ) : (
          <ul className="acm-items">
            {aircrafts.map(ac => (
              <li key={ac.aircraftUid}>
                <button
                  className={`acm-item${selected?.aircraftUid === ac.aircraftUid && mode === 'view' ? ' active' : ''}`}
                  onClick={() => selectAircraft(ac)}
                >
                  <div className="acm-item-top">
                    <span className="acm-item-name">{ac.modelName}</span>
                    <span className={`acm-badge ac-status-${ac.status}`}>
                      {STATUS_LABELS[ac.status] ?? ac.status}
                    </span>
                  </div>
                  <div className="acm-item-id">{ac.aircraftId}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── 상세 / 수정 / 생성 ── */}
      <div className="acm-detail">
        {mode === 'create' ? (
          <div className="acm-form">
            <div className="acm-form-hd">기체 등록</div>
            <div className="acm-section-label">기체 정보</div>
            <div className="acm-row">
              <label>기체 ID *</label>
              <input type="text" value={createForm.aircraftId}
                onChange={e => setCreate('aircraftId', e.target.value)}
                className="acm-input" autoFocus placeholder="예: UAV-001" />
            </div>
            <div className="acm-row">
              <label>모델명 *</label>
              <input type="text" value={createForm.modelName}
                onChange={e => setCreate('modelName', e.target.value)}
                className="acm-input" />
            </div>
            <div className="acm-row">
              <label>제조사 *</label>
              <input type="text" value={createForm.manufacturer}
                onChange={e => setCreate('manufacturer', e.target.value)}
                className="acm-input" />
            </div>
            <div className="acm-row">
              <label>기체 타입</label>
              <select value={createForm.vehicleType}
                onChange={e => setCreate('vehicleType', e.target.value)}
                className="acm-input">
                {Object.entries(VEHICLE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label>최대 속도(kt)</label>
              <input type="number" value={createForm.maxSpeed}
                onChange={e => setCreate('maxSpeed', e.target.value)}
                className="acm-input" min={0} />
            </div>
            <div className="acm-row">
              <label>상태</label>
              <select value={createForm.status}
                onChange={e => setCreate('status', e.target.value)}
                className="acm-input">
                <option value={1}>운영중</option>
                <option value={0}>중단</option>
              </select>
            </div>

            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}

            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleCreate} disabled={saving}>
                {saving ? '등록 중...' : '등록'}
              </button>
              <button className="acm-cancel-btn" onClick={() => { setMode('view'); setMsg({ text: '', type: '' }); }}>
                취소
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="acm-placeholder">좌측 목록에서 기체를 선택하세요.</div>
        ) : (
          <div className="acm-form">
            <div className="acm-form-hd">{selected.aircraftId} 기체 정보</div>

            <div className="acm-section-label">기본 정보</div>
            <div className="acm-row">
              <label>기체 ID</label>
              <input type="text" value={selected.aircraftId} readOnly className="acm-input readonly" />
            </div>
            <div className="acm-row">
              <label>등록일</label>
              <input type="text" value={fmtDate(selected.regDate)} readOnly className="acm-input readonly" />
            </div>

            <div className="acm-section-label">수정</div>
            <div className="acm-row">
              <label>모델명</label>
              <input type="text" value={editForm.modelName}
                onChange={e => setEdit('modelName', e.target.value)}
                className="acm-input" />
            </div>
            <div className="acm-row">
              <label>제조사</label>
              <input type="text" value={editForm.manufacturer}
                onChange={e => setEdit('manufacturer', e.target.value)}
                className="acm-input" />
            </div>
            <div className="acm-row">
              <label>기체 타입</label>
              <select value={editForm.vehicleType}
                onChange={e => setEdit('vehicleType', e.target.value)}
                className="acm-input">
                {Object.entries(VEHICLE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label>최대 속도(kt)</label>
              <input type="number" value={editForm.maxSpeed}
                onChange={e => setEdit('maxSpeed', e.target.value)}
                className="acm-input" min={0} />
            </div>
            <div className="acm-row">
              <label>상태</label>
              <select value={editForm.status}
                onChange={e => setEdit('status', e.target.value)}
                className="acm-input">
                <option value={1}>운영중</option>
                <option value={0}>중단</option>
              </select>
            </div>

            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}

            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '수정'}
              </button>
              {confirmDelete ? (
                <>
                  <span className="acm-del-confirm">정말 삭제하시겠습니까?</span>
                  <button className="acm-save-btn acm-del-ok" onClick={handleDelete} disabled={saving}>
                    확인
                  </button>
                  <button className="acm-cancel-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                </>
              ) : (
                <button className="acm-cancel-btn acm-del-btn" onClick={() => setConfirmDelete(true)}>
                  삭제
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
